'use client';

import { createRef, ReactNode, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Component } from '@headless-workspace/typings';
import { checkOverflow, HtmlHelpers, ScrollPosition } from '@headless-workspace/utils';
import { GlowClickable, GlowIcon, Icons, PropsWithAriaLabel, PropsWithOnClick } from '../../../../src';
import { GlowCTA } from '../cta/GlowCTA';

type NavigationButtonProps = Required<PropsWithAriaLabel> &
    Pick<PropsWithOnClick, 'onClick'> & {
        reverse?: boolean;
    };

const NavigationButton: Component<NavigationButtonProps> = ({ ariaLabel, onClick, reverse }) => (
    <GlowClickable
        className={clsx(
            reverse ? 'left-0 rotate-180' : 'right-0',
            'top-0.5',
            'absolute z-paletteSelectorNavigationButton p-0.5 border-medium border-solid border-border-primary rounded-0.5 bg-surface-primary',
            'hidden group-hover:block',
        )}
        ariaLabel={ariaLabel}
        content={{ icon: <GlowIcon Icon={Icons.ChevronRight} /> }}
        onClick={onClick}
    />
);

/** w-paletteItem (40 px) + gap-0.5 (8 px) = 48 px */
const PALETTE_ITEM_WIDTH = 48;

type WithPaletteItem = {
    id: string;
    ariaLabel: string;
};

type GlowPaletteProps<T extends WithPaletteItem> = {
    previousButtonAriaLabel: string;
    nextButtonAriaLabel: string;
    items: T[];
    renderItem: (item: T, index: number) => ReactNode;
    onClick: (id: string) => void;
    itemContainerClassName?: string;
    onMouseEnter?: (id: string) => void;
    onMouseLeave?: (id: string) => void;
};

/**
 *  A component that renders a product palette variant.
 * @param previousButtonAriaLabel - The previous navigation button aria label.
 * @param nextButtonAriaLabel - The next navigation button aria label.
 * @param items - The items to render.
 * @param renderItem - The render function for each item.
 * @param onClick - The click event handler for each item.
 * @param itemContainerClassName - Additional style for each item. The className will be added to the <button />.
 */
export const GlowPalette = <T extends WithPaletteItem>({
    previousButtonAriaLabel,
    nextButtonAriaLabel,
    items,
    renderItem,
    onClick,
    onMouseEnter,
    onMouseLeave,
    itemContainerClassName,
}: GlowPaletteProps<T>): ReactNode => {
    const [showPrevButton, setShowPrevButton] = useState(false);
    const [showNextButton, setShowNextButton] = useState(true);

    const scrollContainerRef = createRef<HTMLDivElement>();

    const scrollToItem = (type: 'prev' | 'next') => {
        const ulElement = scrollContainerRef.current;

        if (ulElement) {
            const scrollAmount =
                type === 'next' ? ulElement.scrollLeft + PALETTE_ITEM_WIDTH : ulElement.scrollLeft - PALETTE_ITEM_WIDTH;

            ulElement.scrollTo({
                left: scrollAmount,
                behavior: 'smooth',
            });
        }
    };

    const prevButtonHandler = () => scrollToItem('prev');
    const nextButtonHandler = () => scrollToItem('next');

    useEffect(() => {
        const ulElement = scrollContainerRef.current;
        const checkVisibility = () => {
            if (ulElement) {
                const isOverflowing = checkOverflow(ulElement, 'x');

                if (isOverflowing) {
                    const checkScrollPositionListener = () => {
                        const scrollPosition = HtmlHelpers.getHorizontalScrollPosition(ulElement);
                        setShowPrevButton(scrollPosition !== ScrollPosition.Start);
                        setShowNextButton(scrollPosition !== ScrollPosition.End);
                    };
                    checkScrollPositionListener();
                    ulElement.addEventListener('scroll', checkScrollPositionListener);

                    return () => {
                        ulElement.removeEventListener('scroll', checkScrollPositionListener);
                    };
                } else {
                    setShowPrevButton(false);
                    setShowNextButton(false);
                }
            }
        };
        checkVisibility();
        window.addEventListener('resize', checkVisibility);

        return () => {
            window.removeEventListener('resize', checkVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMouseEnter = useCallback(
        (id: string) => () => {
            if (onMouseEnter) {
                onMouseEnter(id);
            }
        },
        [onMouseEnter],
    );

    const handleMouseLeave = useCallback(
        (id: string) => () => {
            if (onMouseLeave) {
                onMouseLeave(id);
            }
        },
        [onMouseLeave],
    );

    return (
        <div className={'relative w-full group px-0.75 py-0.5'}>
            {showPrevButton && (
                <NavigationButton onClick={prevButtonHandler} ariaLabel={previousButtonAriaLabel} reverse />
            )}
            <div
                className={
                    'relative flex flex-row items-center scrollbar-hide overflow-x-scroll overflow-y-hidden h-[2.5rem] '
                }
                ref={scrollContainerRef}
            >
                <ul className={'flex gap-0.5 absolute top-0 h-full'}>
                    {items.map((item, index) => (
                        <li key={`palette-item-${item.id}`} className={'flex'}>
                            <GlowCTA
                                className={clsx('w-[2.5rem] h-[2.5rem]', itemContainerClassName)}
                                onClick={() => onClick(item.id)}
                                ariaLabel={item.ariaLabel}
                                onMouseEnter={handleMouseEnter(item.id)}
                                onMouseLeave={handleMouseLeave(item.id)}
                            >
                                {renderItem(item, index)}
                            </GlowCTA>
                        </li>
                    ))}
                </ul>
            </div>
            {showNextButton && <NavigationButton onClick={nextButtonHandler} ariaLabel={nextButtonAriaLabel} />}
        </div>
    );
};
